import { useCallback, useEffect, useState } from 'react';
import { useThemeQueries } from 'views';
import { AccordionSaurus } from 'views/components/accordions/accordion-saurus/accordion-saurus';
import { Box, Button, Divider, Grid, Slider } from '@material-ui/core';
import { Controller, useForm } from 'react-hook-form';
import { TextFieldSaurus } from 'views/components/controles/inputs';
import { IndicacoesModel } from 'model/api/gestao/indicacoes/indicacoes-model';
import { SalvarEditarIcon } from 'views/components/icons';
import { IndicacoesPutModel } from 'model/api/gestao/indicacoes/indicacoes-put-model';
import { useSessaoAtual } from 'services/app';
import { EnumTipoPessoas } from 'model/enums/enum-tipo-pessoas';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAccordionAvaliacaoAgenteValidation } from './accordion-agente-validation';
import { useStyles } from './accordion-agente-styles';
import { EnumStatusIndicacao } from 'model/enums/enum-status-indicacao';

interface AccordionIndicacaoAvaliacaoAgenteProps {
    indicacoes: IndicacoesModel;
    carreagando: boolean;
    hasValor: boolean;
    onSubmit: (valor: IndicacoesPutModel) => Promise<void>;
}

export const AccordionIndicacaoAvaliacaoAgente = ({
    indicacoes,
    carreagando,
    hasValor,
    onSubmit
}: AccordionIndicacaoAvaliacaoAgenteProps) => {
    const { theme } = useThemeQueries()
    const { tipoUsuario } = useSessaoAtual()
    const { FormAvalicacaoAgenteValidationYup } = useAccordionAvaliacaoAgenteValidation();
    const classes = useStyles()

    const statusAlterado = useCallback((): boolean => {
        if (tipoUsuario() === EnumTipoPessoas.Revendedor)
            return true;

        if (indicacoes.status === EnumStatusIndicacao.PropostaAceita
            || indicacoes.status === EnumStatusIndicacao.PropostaRecusada
            || indicacoes.status === EnumStatusIndicacao.Cancelado)
            return false;

        return true
    }, [indicacoes.status, tipoUsuario])

    const [openAccordion, setOpenAccordion] = useState<boolean>(true)

    const {
        handleSubmit,
        formState: { errors },
        control,
        reset,
        getValues,
        setValue,
    } = useForm<IndicacoesModel>({
        defaultValues: { ...indicacoes },
        resolver: yupResolver(FormAvalicacaoAgenteValidationYup),
        criteriaMode: 'all',
        mode: 'onBlur' && 'onChange',
    });

    useEffect(() => {
        reset({ ...indicacoes })
    }, [indicacoes, reset])

    const submit = async (valor: IndicacoesModel) => {
        const indicacaoPut: IndicacoesPutModel = {
            indicadorId: indicacoes.indicador?.id || '',
            responsavelId: indicacoes.responsavel?.id || null,
            instrucao: indicacoes.instrucao,
            motivoAvaliacaoResponsavel: indicacoes.motivoAvaliacaoResponsavel,
            motivoAvaliacaoIndicador: valor.motivoAvaliacaoIndicador,
            avaliacaoIndicador: Number(valor.avaliacaoIndicador),
            sistemaId: indicacoes.sistema.id || null,
            avaliacaoResponsavel: indicacoes.avaliacaoResponsavel,
            nivelDeInteresse: indicacoes.nivelDeInteresse,
            observacaoFinalizacao: indicacoes.observacaoFinalizacao,
            id: indicacoes.id,
            data: indicacoes.data,
            status: indicacoes.status,
            faturamentoEstimado: indicacoes.faturamentoEstimado
        }

        await onSubmit(indicacaoPut)
    }

    const marks = [
        {
            value: 0,
            label: '0',
        },
        {
            value: 10,
            label: '10',
        },
    ];

    return (
        <>
            <div>
                <AccordionSaurus
                    labelPrimary="Avaliação do Agente"
                    tipoExpand="bold"
                    noPaperRoot={false}
                    heightDivider={2}
                    showDivider={openAccordion}
                    colorDivider={theme.palette.primary.main}
                    colorExpand={theme.palette.primary.main}
                    expanded={openAccordion}
                    onChange={() => setOpenAccordion(prev => !prev)}
                >
                    <form onSubmit={handleSubmit(submit)} style={{ width: '100%' }}>
                        <Grid container spacing={3} style={{
                            position: 'relative'
                        }}>
                            <Divider variant='fullWidth' />
                            <Grid item xs={12}>
                                {/* <Controller
                                    name="avaliacaoIndicador"
                                    control={control}
                                    render={({ field }) => (
                                        <TextFieldSaurus
                                            allowSubmit
                                            readOnly={statusAlterado()}
                                            tipo="NUMERO"
                                            label="Nota de Avaliação"
                                            fullWidth
                                            helperText={
                                                errors.avaliacaoIndicador
                                                    ? errors.avaliacaoIndicador.message
                                                    : undefined
                                            }
                                            error={Boolean(errors.avaliacaoIndicador && errors.avaliacaoIndicador.message)}
                                            autoComplete={'off'}
                                            {...field}
                                        />
                                    )}
                                /> */}
                                <Box style={{ padding: '0 8px' }}>
                                    <Controller
                                        control={control}
                                        name="avaliacaoIndicador"
                                        render={({ field }) => (
                                            <Slider
                                                min={0}
                                                max={10}
                                                className={classes.slider}
                                                disabled={statusAlterado()}
                                                value={getValues('avaliacaoIndicador')}
                                                aria-labelledby="discrete-slider-always"
                                                valueLabelDisplay={hasValor ? "on" : "auto"}
                                                marks={marks}
                                                onChange={(ev, newValue) => setValue('avaliacaoIndicador', Number(newValue))}
                                            />
                                        )}
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Controller
                                    name="motivoAvaliacaoIndicador"
                                    control={control}
                                    render={({ field }) => (
                                        <TextFieldSaurus
                                            allowSubmit
                                            label="Motivo da Avaliação"
                                            readOnly={statusAlterado()}
                                            fullWidth
                                            multiline
                                            size='medium'
                                            minRows={1}
                                            maxRows={6}
                                            helperText={
                                                errors.motivoAvaliacaoIndicador
                                                    ? errors.motivoAvaliacaoIndicador.message
                                                    : undefined
                                            }
                                            error={Boolean(errors.motivoAvaliacaoIndicador && errors.motivoAvaliacaoIndicador.message)}
                                            autoComplete={'off'}
                                            {...field}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container justifyContent="flex-end">
                                    <Grid item xs={12} md={5} lg={4}>
                                        <Button variant="contained" color='primary' size='large' type="submit" fullWidth>
                                            <SalvarEditarIcon tipo="BUTTON_PRIMARY" />
                                            Salvar
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </form>
                </AccordionSaurus>
            </div>
        </>
    );
}